import React, { useCallback } from 'react';
import isNil from 'lodash/isNil';
import Button from '@atlaskit/button';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { CSV, type ImportType } from '../../../common/constants/import-type';
import { useUploadedFileName } from '../../../controllers/import-state';
import { Dropzone } from './dropzone';
import messages from './messages';

type Props = {
	onNext: (importType?: ImportType) => void;
	onClose: () => void;
	isCompact?: boolean;
};

export const UploadCSVDropZone = ({ onNext, onClose, isCompact }: Props) => {
	const { formatMessage } = useIntl();
	const [uploadedFileName] = useUploadedFileName();

	const onContinue = useCallback(() => {
		onNext(CSV);
	}, [onNext]);

	if (!isCompact) {
		return <Dropzone isLeaner={!isCompact} />;
	}

	return (
		<Stack>
			<Box xcss={headingStyles} paddingBlockStart="space.300">
				{formatMessage(messages.uploadCsvDropzoneHeading)}
			</Box>
			<Dropzone />
			<Flex justifyContent="end" gap="space.100" xcss={buttonGroupStyles}>
				<Button
					testId="project-create-importer-upload-flow.ui.upload-screen-standard.upload-csv-dropzone.cancel-button"
					appearance="subtle"
					onClick={onClose}
				>
					{formatMessage(messages.uploadFooterCancel)}
				</Button>
				<Button
					appearance="primary"
					onClick={onContinue}
					isDisabled={isNil(uploadedFileName)}
					testId="project-create-importer-upload-flow.ui.upload-screen-standard.upload-csv-dropzone.next-button"
				>
					{formatMessage(messages.uploadFooterContinue)}
				</Button>
			</Flex>
		</Stack>
	);
};

const headingStyles = xcss({
	color: 'color.text',
	font: token('font.heading.small'),
});

const buttonGroupStyles = xcss({
	paddingBlockStart: 'space.200',
	paddingBlockEnd: 'space.300',
	marginBlockStart: 'space.250',
});
